import React from 'react';
import { Edit, Copy, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

/**
 * FormulaCard - Composant réutilisable pour afficher toutes les formules
 * Style unifié basé sur les cartes Petit Déjeuner
 */
const FormulaCard = ({
    formula,
    managerMode = false,
    canEdit = false,
    onAddToCart,
    onEdit,
    onDuplicate,
    onDelete,
}) => {
    const handleClick = () => {
        if (!managerMode && onAddToCart) {
            onAddToCart(formula);
        }
    };

    return (
        <div
            onClick={handleClick}
            data-testid={`formula-card-${formula.id}`}
            className={cn(
                // Base styles
                "group relative rounded-xl border overflow-hidden cursor-pointer select-none",
                // Padding et structure
                "p-4 min-h-[120px]",
                // Couleurs et bordures
                "bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700",
                // Transitions et animations
                "transition-all duration-150 ease-out",
                "hover:shadow-lg hover:border-emerald-400 dark:hover:border-emerald-500",
                "active:scale-[0.98]",
                // Mode Manager - bordure de surbrillance
                managerMode && "ring-2 ring-emerald-400/50"
            )}
        >
            {/* Header: Titre + Badge Prix */}
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                    {/* Badge spécial (Populaire, Premium, etc.) - inline with title */}
                    {formula.badge && (
                        <Badge 
                            variant="secondary" 
                            className={cn(
                                "text-[10px] font-semibold mb-1 inline-block",
                                formula.badge === 'Populaire' && "bg-yellow-100 text-yellow-700 border-yellow-300",
                                formula.badge === 'Premium' && "bg-purple-100 text-purple-700 border-purple-300",
                                formula.badge === 'Business' && "bg-blue-100 text-blue-700 border-blue-300",
                                formula.badge === 'Gourmand' && "bg-orange-100 text-orange-700 border-orange-300",
                                formula.badge === 'Recommandé' && "bg-green-100 text-green-700 border-green-300",
                                !['Populaire', 'Premium', 'Business', 'Gourmand', 'Recommandé'].includes(formula.badge) && "bg-gray-100 text-gray-700"
                            )}
                        >
                            {formula.badge}
                        </Badge>
                    )}
                    <h3 className="font-bold text-base text-gray-900 dark:text-white truncate">
                        {formula.name}
                    </h3>
                    {formula.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {formula.description}
                        </p>
                    )}
                </div>
                
                {/* Badge Prix - toujours visible (sauf en mode manager où les boutons le remplacent) */}
                {!managerMode && (
                    <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white font-bold text-sm px-2.5 py-1 shrink-0">
                        {formula.price?.toLocaleString()} F
                    </Badge>
                )}
                {managerMode && !canEdit && (
                    <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white font-bold text-sm px-2.5 py-1 shrink-0">
                        {formula.price?.toLocaleString()} F
                    </Badge>
                )}
            </div>

            {/* Étapes de sélection (steps) */}
            {formula.steps && formula.steps.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                    {formula.steps.map((step, idx) => (
                        <Badge 
                            key={idx} 
                            variant="outline" 
                            className={cn(
                                "text-[10px] font-medium py-0.5 px-2",
                                "border-emerald-300 dark:border-emerald-600",
                                "text-emerald-700 dark:text-emerald-400",
                                "bg-emerald-50 dark:bg-emerald-900/20"
                            )}
                        >
                            {step.name}
                        </Badge>
                    ))}
                </div>
            )}

            {/* Composants simples (pour formules sans steps) */}
            {(!formula.steps || formula.steps.length === 0) && formula.components && formula.components.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
                    {formula.components.map((comp, idx) => (
                        <Badge 
                            key={idx} 
                            variant="outline" 
                            className="text-[10px] font-medium py-0.5 px-2 border-gray-300 text-gray-600 dark:text-gray-400"
                        >
                            {comp.name || comp.product_name}
                        </Badge>
                    ))}
                </div>
            )}

            {/* Boutons d'action - Mode Manager uniquement */}
            {managerMode && canEdit && (
                <div className="absolute top-2 right-2 flex items-center gap-1.5 bg-white/95 dark:bg-zinc-800/95 backdrop-blur-sm rounded-lg px-2 py-1.5 shadow-lg border border-gray-200 dark:border-zinc-600">
                    {/* Éditer */}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 active:scale-95 transition-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.(formula);
                        }}
                        title="Modifier"
                    >
                        <Edit className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </Button>
                    
                    {/* Dupliquer */}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900/40 active:scale-95 transition-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDuplicate?.(formula);
                        }}
                        title="Dupliquer"
                    >
                        <Copy className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </Button>
                    
                    {/* Supprimer */}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 hover:bg-red-100 dark:hover:bg-red-900/40 active:scale-95 transition-all opacity-60 hover:opacity-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.(formula.id, formula.name);
                        }}
                        title="Supprimer"
                    >
                        <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                    </Button>
                </div>
            )}
        </div>
    );
};

export default FormulaCard;
